import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { RiskBadge } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { EvidenceBadge } from '@/components/common/EvidenceBadge';
import { ObservationStageBadge } from '@/components/common/ObservationStageBadge';
import { SimulationDisclaimer } from '@/components/common/SimulationDisclaimer';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';
import { BeforeAfterViewer } from '@/components/detection/BeforeAfterViewer';
import { Progress } from '@/components/ui/progress';
import { api } from '@/lib/api';
import { fmtDate, fmtPct } from '@/lib/format';
import { fmtDistance } from '@/lib/format2';
import { canValidate, useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, ArrowRight, HelpCircle, Info } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

export default function DetectionDetailPage() {
  const { detId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [det, setDet] = useState(null);
  const [asset, setAsset] = useState(null);
  const [baseObs, setBaseObs] = useState(null);
  const [newObs, setNewObs] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  usePageTitle(det ? `Observation · ${det.change_type}` : 'Observation');

  const load = async () => {
    const d = await api.get(`/detections/${detId}`).then(r => r.data);
    setDet(d);
    setNotes(d.validator_notes || '');
    const [a, b, n] = await Promise.all([
      api.get(`/assets/${d.asset_id}`).then(r => r.data),
      api.get(`/observations/${d.baseline_observation_id}`, { params: { include_image: true } }).then(r => r.data),
      api.get(`/observations/${d.new_observation_id}`, { params: { include_image: true } }).then(r => r.data),
    ]);
    setAsset(a); setBaseObs(b); setNewObs(n);
  };
  useEffect(() => { load(); }, [detId]);

  const validate = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/detections/${detId}/validate`, { validation_status: status, notes });
      setDet(data);
      toast.success(`AI observation marked “${status}”`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Validation failed');
    } finally { setBusy(false); }
  };

  if (!det || !asset) return <div className="p-6"><PageLoader /></div>;

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <PageHeader
        title={<span className="flex flex-wrap items-center gap-3"><CategoryBadge category={det.change_type} /> AI observation</span>}
        subtitle={<span className="flex items-center gap-2"><Link to={`/app/assets/${asset.id}`} className="hover:text-primary">{asset.name}</Link>{det.is_simulation ? <SimulationDisclaimer compact /> : <VerifiedBadge />}</span>}
        actions={<div className="flex items-center gap-2"><ObservationStageBadge stage={det.observation_stage} /><RiskBadge severity={det.severity} /></div>}
      />

      {det.is_simulation ? <div className="mb-4"><SimulationDisclaimer /></div> : <div className="mb-4"><VerifiedBadge variant="full" /></div>}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {baseObs?.image_b64 && newObs?.image_b64 && (
            <BeforeAfterViewer
              before={baseObs.image_b64}
              after={newObs.image_b64}
              overlayB64={det.overlay_jpg_b64}
              meta={{
                baseline: baseObs.acquired_at,
                after: newObs.acquired_at,
                source: newObs.data_source,
                resolution: newObs.spatial_resolution_m,
              }}
              detection={det}
            />
          )}

          <Card className="p-4">
            <div className="text-sm font-semibold">AI interpretation</div>
            <p className="mt-2 text-sm">{det.description}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(det.affected_features || []).map(f => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
            </div>
            {det.recommended_action && (
              <div className="mt-3 text-xs"><b>Recommended:</b> <span className="text-muted-foreground">{det.recommended_action}</span></div>
            )}
            <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground flex items-start gap-1.5">
              <Info size={12} className="shrink-0 mt-0.5" />
              <span>Confidence is the AI's certainty in its interpretation. It is not the probability that an incident has occurred. Analyst validation is required before action.</span>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-4">
            <div className="text-sm font-semibold">Scoring</div>
            <div className="mt-3 space-y-3">
              <ScoreBar label="AI confidence" value={Math.round((det.llm_confidence || 0) * 100)} />
              <ScoreBar label="Risk score" value={det.risk_score} />
              <ScoreBar label="Affected area" value={Math.min(100, Math.round(det.affected_area_pct * 5))} display={fmtPct(det.affected_area_pct)} />
            </div>
            <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <Row k="Regions" v={<span className="font-mono">{det.num_regions}</span>} />
              <Row k="Distance to asset" v={<span className="font-mono">{fmtDistance(det.distance_to_asset_m)}</span>} />
              <Row k="Model" v={<span className="font-mono">{det.model_name}</span>} />
              <Row k="Created" v={fmtDate(det.created_at)} />
              <Row k="Evidence" v={<EvidenceBadge quality={det.evidence_quality} />} />
              <Row k="Stage" v={<ObservationStageBadge stage={det.observation_stage} />} />
            </div>
          </Card>

          {(det.risk_factors || []).length > 0 && (
            <Card className="p-4">
              <div className="text-sm font-semibold">Risk factors</div>
              <div className="mt-2 text-[11px] text-muted-foreground">Contributing factors to the risk score:</div>
              <div className="mt-2 divide-y divide-border">
                {det.risk_factors.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 text-xs">
                    <span className="flex-1">{f.name}</span>
                    <span className="font-mono tabular-nums text-muted-foreground">{f.display}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-4">
            <div className="text-sm font-semibold">Analyst validation</div>
            <div className="mt-2 text-xs">Current: <Badge variant="outline" className="text-[10px]">{det.validation_status}</Badge></div>
            {det.validated_at && <div className="text-[10px] text-muted-foreground mt-1">Last validated {fmtDate(det.validated_at)}</div>}
            {canValidate(user.role) && (
              <>
                <Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Analyst notes…" className="mt-3" data-testid="validation-notes" />
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button size="sm" onClick={() => validate('Confirmed')} disabled={busy} data-testid="detection-validate-button">
                    <CheckCircle2 size={14} className="mr-1" /> Confirm
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => validate('Likely')} disabled={busy}>
                    <ArrowRight size={14} className="mr-1" /> Likely
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => validate('Needs review')} disabled={busy}>
                    <HelpCircle size={14} className="mr-1" /> Needs review
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => validate('False positive')} disabled={busy} data-testid="detection-reject-button">
                    <XCircle size={14} className="mr-1" /> False positive
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) { return (<><div className="text-muted-foreground">{k}</div><div className="text-right">{v}</div></>); }
function ScoreBar({ label, value, display }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono tabular-nums">{display ?? `${value}%`}</span>
      </div>
      <Progress value={value} className="h-1.5" />
    </div>
  );
}
